import { getAuthToken, setAuthToken, removeAuthToken } from './api';
import toastService from './ToastService';

// Import the configured API client instead of using raw axios
import axios from 'axios';

// Determine the correct base URL based on environment
const getBaseURL = () => {
  // Check if we're in development (localhost)
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return '/api'; // Use proxy in development
  }
  
  // Check for environment variable first
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }
  
  // Production fallback - use the correct Vercel backend URL
  return 'https://sabiops-backend.vercel.app/api';
};

const API_BASE_URL = getBaseURL();

const subscriptionService = {
  /**
   * Verify a payment and update the user's subscription
   * @param {string} reference - The payment reference from Paystack
   * @param {string} planId - The ID of the plan being subscribed to
   * @returns {Promise<Object>} The updated subscription data
   */
  verifyPayment: async (reference, planId) => {
    try {
      console.log(`[SubscriptionService] Verifying payment for plan ${planId} with reference ${reference}`);

      const response = await axios.post(
        `${API_BASE_URL}/subscription/verify-payment`,
        { reference, plan_id: planId },
        {
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`,
            'Content-Type': 'application/json',
          },
          timeout: 30000, // 30 seconds timeout
        }
      );

      // If we get a new token in the response, update it
      if (response.data.data && response.data.data.access_token) {
        console.log('[SubscriptionService] Received new access token, updating...');

        // Store the new token
        setAuthToken(response.data.data.access_token);

        // Update axios default headers with new token
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.data.access_token}`;

        // Notify any listeners that the token was updated
        if (window.dispatchEvent) {
          const event = new CustomEvent('tokenUpdated', {
            detail: {
              token: response.data.data.access_token,
              subscription: response.data.data.subscription
            }
          });
          window.dispatchEvent(event);
        }

        // Trigger subscription update event
        const subscriptionEvent = new CustomEvent('subscriptionUpdated', {
          detail: {
            subscription: response.data.data.subscription,
            plan_config: response.data.data.plan_config,
            usage_reset: response.data.data.usage_reset
          }
        });
        window.dispatchEvent(subscriptionEvent);

        // Trigger usage status refresh
        const usageEvent = new CustomEvent('usageStatusUpdated', {
          detail: {
            subscription: response.data.data.subscription,
            usage_reset: response.data.data.usage_reset
          }
        });
        window.dispatchEvent(usageEvent);
      }

      console.log('[SubscriptionService] Payment verification successful:', response.data);
      return response.data;

    } catch (error) {
      console.error('[SubscriptionService] Error verifying payment:', error);

      // Handle specific error cases
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);

        // If unauthorized, clear the token
        if (error.response.status === 401 || error.response.status === 403) {
          removeAuthToken();
          toastService.error('Your session has expired. Please log in again.');
        }

        throw error.response.data || {
          error: error.response.statusText || 'Payment verification failed'
        };
      } else if (error.request) {
        // The request was made but no response was received
        console.error('No response received:', error.request);
        throw { error: 'No response from server. Please check your connection.' };
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('Error setting up request:', error.message);
        throw { error: error.message || 'Failed to process payment verification' };
      }
    }
  },

  /**
   * Get the current subscription status for the authenticated user
   * @returns {Promise<Object>} The subscription status data
   */
  getSubscriptionStatus: async () => {
    try {
      const token = getAuthToken();
      console.log('[SubscriptionService] Fetching subscription status...');
      console.log('[SubscriptionService] Token:', token);
      console.log('[SubscriptionService] Token type:', typeof token);
      console.log('[SubscriptionService] Token value:', JSON.stringify(token));

      // Check if token is null or "null" string
      if (!token || token === 'null' || token === 'undefined') {
        console.error('[SubscriptionService] Invalid token detected:', token);
        throw new Error('No valid authentication token found');
      }

      const response = await axios.get(
        `${API_BASE_URL}/subscription/unified-status`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          timeout: 10000, // 10 seconds timeout
        }
      );

      console.log('[SubscriptionService] Subscription status:', response.data);
      return response.data.data || response.data;

    } catch (error) {
      console.error('[SubscriptionService] Error fetching subscription status:', error);

      if (error.response) {
        if (error.response.status === 401 || error.response.status === 403) {
          removeAuthToken();
          toastService.error('Your session has expired. Please log in again.');
        }
        throw error.response.data || { error: 'Failed to fetch subscription status' };
      }

      throw { error: error.message || 'Failed to fetch subscription status' };
    }
  },

  /**
   * Get real-time subscription status with automatic day countdown
   * @returns {Promise<Object>} The real-time subscription status data
   */
  getCurrentSubscription: async () => {
    try {
      const token = getAuthToken();
      console.log('[SubscriptionService] Fetching real-time subscription status...');

      // Check if token is null or "null" string
      if (!token || token === 'null' || token === 'undefined') {
        console.error('[SubscriptionService] Invalid token detected:', token);
        throw new Error('No valid authentication token found');
      }

      const response = await axios.get(
        `${API_BASE_URL}/subscription/realtime-status`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          timeout: 10000, // 10 seconds timeout
        }
      );

      const data = response.data.data || response.data;
      console.log('[SubscriptionService] Real-time subscription data:', data);
      return data;

    } catch (error) {
      console.error('[SubscriptionService] Error fetching real-time subscription:', error);

      if (error.response) {
        if (error.response.status === 401 || error.response.status === 403) {
          removeAuthToken();
          toastService.error('Your session has expired. Please log in again.');
        }
      }

      // Return free plan as fallback
      return {
        plan_name: 'Free Plan',
        plan_id: 'free',
        status: 'active',
        days_remaining: null,
        is_expired: false,
        is_expiring_soon: false,
        is_trial: false,
        features: [
          'Basic invoicing',
          'Up to 10 products',
          'Basic reporting'
        ],
        limits: {
          invoices: 10,
          products: 10,
          customers: 25,
          sales: 50,
          storage_mb: 100
        },
        display_message: 'You\'re on the free plan. Upgrade to unlock more features.',
        last_updated: new Date().toISOString()
      };
    }
  },

  /**
   * Check if user has access to a specific feature
   * @param {string} feature - The feature to check access for
   * @returns {Promise<Object>} The feature access data
   */
  checkFeatureAccess: async (feature) => {
    try {
      const token = getAuthToken();
      console.log(`[SubscriptionService] Checking ${feature} access...`);

      if (!token || token === 'null' || token === 'undefined') {
        console.error('[SubscriptionService] Invalid token detected:', token);
        throw new Error('No valid authentication token found');
      }

      const response = await axios.get(
        `${API_BASE_URL}/subscription/feature-access/${feature}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          timeout: 10000, // 10 seconds timeout
        }
      );

      const data = response.data.data || response.data;
      console.log(`[SubscriptionService] Feature ${feature} access:`, data);
      return data;

    } catch (error) {
      console.error(`[SubscriptionService] Error checking ${feature} access:`, error);

      if (error.response) {
        if (error.response.status === 401 || error.response.status === 403) {
          removeAuthToken();
          toastService.error('Your session has expired. Please log in again.');
        }
      }

      // Default to no access on error
      return {
        has_access: false,
        current_usage: 0,
        feature_limit: 0,
        usage_percentage: 100,
        plan_name: 'Unknown',
        is_expired: true
      };
    }
  },

  /**
   * Get the current usage status for the authenticated user
   * @returns {Promise<Object>} The usage status data
   */
  getUsageStatus: async () => {
    try {
      const token = getAuthToken();
      console.log('[SubscriptionService] Fetching usage status...');
      console.log('[SubscriptionService] Token:', token);
      console.log('[SubscriptionService] Token type:', typeof token);
      console.log('[SubscriptionService] Token value:', JSON.stringify(token));

      // Check if token is null or "null" string
      if (!token || token === 'null' || token === 'undefined') {
        console.error('[SubscriptionService] Invalid token detected:', token);
        throw new Error('No valid authentication token found');
      }

      const response = await axios.get(
        `${API_BASE_URL}/subscription/usage-status`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          timeout: 10000, // 10 seconds timeout
        }
      );

      console.log('[SubscriptionService] Usage status:', response.data);
      return response.data.data || response.data;

    } catch (error) {
      console.error('[SubscriptionService] Error fetching usage status:', error);

      if (error.response) {
        if (error.response.status === 401 || error.response.status === 403) {
          removeAuthToken();
          toastService.error('Your session has expired. Please log in again.');
        }
        throw error.response.data || { error: 'Failed to fetch usage status' };
      }

      throw { error: error.message || 'Failed to fetch usage status' };
    }
  },

  /**
   * Get available subscription plans
   * @returns {Promise<Array>} List of available subscription plans
   */
  getPlans: async () => {
    try {
      console.log('[SubscriptionService] Fetching subscription plans...');

      const response = await axios.get(
        `${API_BASE_URL}/subscription/plans`,
        {
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`,
            'Content-Type': 'application/json',
          },
          timeout: 10000, // 10 seconds timeout
        }
      );

      console.log('[SubscriptionService] Subscription plans:', response.data);
      return response.data.data || response.data;

    } catch (error) {
      console.error('[SubscriptionService] Error fetching subscription plans:', error);

      if (error.response) {
        if (error.response.status === 401 || error.response.status === 403) {
          removeAuthToken();
          toastService.error('Your session has expired. Please log in again.');
        }
        throw error.response.data || { error: 'Failed to fetch subscription plans' };
      }

      throw { error: error.message || 'Failed to fetch subscription plans' };
    }
  },
};

export default subscriptionService;
